import { Box, Chip, CircularProgress, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import PointhomeContentStatusKpiCards from '../../../components/pointhome/contents/status/PointhomeContentStatusKpiCards';
import type { PointhomeContentStatusKpiData } from '../../../components/pointhome/contents/status/PointhomeContentStatusKpiCards';
import RouletteContentUserStatusChart from '../../../components/pointhome/roulette/content/RouletteContentUserStatusChart';
import type { UserStatusDatum } from '../../../components/pointhome/roulette/content/RouletteContentUserStatusChart';
import RouletteContentDailyCountChart from '../../../components/pointhome/roulette/content/RouletteContentDailyCountChart';
import type { DailyCountDatum } from '../../../components/pointhome/roulette/content/RouletteContentDailyCountChart';
import RouletteContentDailyCountTable from '../../../components/pointhome/roulette/content/RouletteContentDailyCountTable';
import type { DailyCountTableRow } from '../../../components/pointhome/roulette/content/RouletteContentDailyCountTable';
import apiClient from '../../../utils/axiosClient';
import type { PointhomeContentStatusData } from '../../../types/pointhomeContentStatus';

dayjs.locale('ko');

const CONTENT_TABS = ['해달 누르기', '해달 청소하기', '해달 설거지하기'] as const;

/** 탭 인덱스 -> 백엔드 content_type (infra/repositories/pointhome/cash_ch_repository.py의 CONTENT_TYPE_MAP과 동일) */
const CONTENT_TAB_TO_TYPE: Record<number, string> = {
  0: 'ph_interact',
  1: 'ph_clean',
  2: 'ph_wash',
};

function formatDelta(value: number, prev: number): { text: string; isUp: boolean } {
  const diff = value - prev;
  const sign = diff >= 0 ? '+' : '';
  if (prev === 0)
    return {
      text: `${value >= 0 ? '+' : ''}${value.toLocaleString('ko-KR')} (—%)`,
      isUp: value >= 0,
    };
  const pct = Math.round((diff / prev) * 100);
  return { text: `${sign}${diff.toLocaleString('ko-KR')} (${sign}${pct}%)`, isUp: diff >= 0 };
}

export default function PointhomeContentStatus() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [contentTab, setContentTab] = useState(0);
  const [data, setData] = useState<PointhomeContentStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...(response.data || [])]);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) {
      setData(null);
      return;
    }
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : (selectedApp ?? 'all');
    setLoading(true);
    setError(null);
    apiClient
      .get<PointhomeContentStatusData>('/api/v1/analytics/pointhome/participation/content', {
        params: {
          start_date: start,
          end_date: end,
          content_type: CONTENT_TAB_TO_TYPE[contentTab],
          app,
          os_type: selectedOs,
        },
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        setData(null);
        setError(err?.response?.data?.detail ?? err?.message ?? '조회 실패');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs, contentTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** 선택한 기간의 일수 (전월 동기간도 같은 일수라고 가정) */
  const daysCount = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return endDate.diff(startDate, 'day') + 1;
  }, [startDate, endDate]);

  const kpiData = useMemo((): PointhomeContentStatusKpiData | null => {
    if (!data || daysCount <= 0) return null;
    const dauDelta = formatDelta(data.totalDAU, data.prevTotalDAU);
    const userDelta = formatDelta(data.totalUserCount, data.prevTotalUserCount);
    const txnDelta = formatDelta(data.totalTransactionCount, data.prevTotalTransactionCount);
    const participationRate =
      data.totalDAU > 0 ? Math.round((data.totalUserCount / data.totalDAU) * 1000) / 10 : 0;
    const prevParticipationRate =
      data.prevTotalDAU > 0
        ? Math.round((data.prevTotalUserCount / data.prevTotalDAU) * 1000) / 10
        : 0;
    /** 획득 평균 포인트: 캐시 지급액 합계 ÷ 참여 완료 횟수 */
    const avgCashPerTxn =
      data.totalTransactionCount > 0
        ? Math.round((data.totalCashSum / data.totalTransactionCount) * 10) / 10
        : 0;
    const prevAvgCashPerTxn =
      data.prevTotalTransactionCount > 0
        ? Math.round((data.prevTotalCashSum / data.prevTotalTransactionCount) * 10) / 10
        : 0;
    /** 1인당 일 평균 획득 포인트: date별 (cashSum ÷ userCount)의 평균 */
    const dailyRatios = (data.dailyUserStats ?? [])
      .filter((row) => row.userCount > 0)
      .map((row) => row.cashSum / row.userCount);
    const avgCashPerUserPerDay =
      dailyRatios.length > 0
        ? Math.round((dailyRatios.reduce((s, v) => s + v, 0) / dailyRatios.length) * 10) / 10
        : 0;
    const prevDailyRatios = (data.prevDailyUserStats ?? [])
      .filter((row) => row.userCount > 0)
      .map((row) => row.cashSum / row.userCount);
    const prevAvgCashPerUserPerDay =
      prevDailyRatios.length > 0
        ? Math.round((prevDailyRatios.reduce((s, v) => s + v, 0) / prevDailyRatios.length) * 10) /
          10
        : 0;
    /** 일 한도 도달률: 최대 참여 도달 사용자 수 ÷ 참여 완료 사용자 수 */
    const maxReachedRate =
      data.totalUserCount > 0
        ? Math.round((data.totalMaxReachedUserCount / data.totalUserCount) * 1000) / 10
        : 0;
    const prevMaxReachedRate =
      data.prevTotalUserCount > 0
        ? Math.round((data.prevTotalMaxReachedUserCount / data.prevTotalUserCount) * 1000) / 10
        : 0;
    return {
      dau: { value: data.totalDAU, delta: dauDelta },
      participatedUser: { value: data.totalUserCount, delta: userDelta },
      transactionCount: { value: data.totalTransactionCount, delta: txnDelta },
      participationRate: {
        value: participationRate,
        delta: formatDelta(participationRate, prevParticipationRate),
      },
      avgCashPerTxn: { value: avgCashPerTxn, delta: formatDelta(avgCashPerTxn, prevAvgCashPerTxn) },
      avgCashPerUserPerDay: {
        value: avgCashPerUserPerDay,
        delta: formatDelta(avgCashPerUserPerDay, prevAvgCashPerUserPerDay),
      },
      maxReachedRate: {
        value: maxReachedRate,
        delta: formatDelta(maxReachedRate, prevMaxReachedRate),
      },
    };
  }, [data, daysCount]);

  const userStatusChartData = useMemo((): UserStatusDatum[] | undefined => {
    if (!data?.dailyUserStats?.length) return undefined;
    return data.dailyUserStats.map((row, i) => ({
      label: String(i + 1),
      dayLabel: dayjs(row.date).format('MM/DD'),
      activeUsers: row.dau ?? 0,
      participationCompletedUsers: row.userCount ?? 0,
    }));
  }, [data]);

  /** 일 참여 횟수(txnCount) 목록: totalDailyTransaction의 키를 오름차순 정렬 */
  const txnCountKeys = useMemo(() => {
    if (!data?.totalDailyTransaction) return [];
    return Object.keys(data.totalDailyTransaction)
      .map(Number)
      .sort((a, b) => a - b);
  }, [data]);

  const dailyCountColumnLabels = useMemo(() => txnCountKeys.map((n) => `${n}회`), [txnCountKeys]);

  const dailyChartData = useMemo((): DailyCountDatum[] | undefined => {
    if (!data?.totalDailyTransaction) return undefined;
    return txnCountKeys.map((n) => ({
      range: `${n}회`,
      value: Number(data.totalDailyTransaction[String(n)] ?? 0),
    }));
  }, [data, txnCountKeys]);

  const dailyTableRows = useMemo((): DailyCountTableRow[] | undefined => {
    if (!data?.dailyTransaction?.length) return undefined;
    return data.dailyTransaction.map((row) => {
      const counts = txnCountKeys.map((n) => Number(row[String(n)] ?? 0) || 0);
      const rowTotal = counts.reduce((s, v) => s + v, 0);
      return {
        date: String(row.date ?? ''),
        ranges: counts.map((count) => ({
          rate: rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0,
          count,
        })),
      };
    });
  }, [data, txnCountKeys]);

  /** 지급 금액별 분포: cashAmountStats(amount 오름차순)를 막대 차트 데이터로 변환 */
  const amountChartData = useMemo((): DailyCountDatum[] | undefined => {
    if (!data?.cashAmountStats?.length) return undefined;
    return [...data.cashAmountStats]
      .sort((a, b) => a.amount - b.amount)
      .map((s) => ({ range: `${s.amount.toLocaleString('ko-KR')}P`, value: s.txnCount }));
  }, [data]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* 상단 필터: 기간, 앱 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 180 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(app) => setSelectedApp(app ?? '앱 전체')}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* 박스: 앱 리스트 → 구분선 -> 탭 */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
          opacity: loading ? 0.3 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        {/* 앱 선택 (Chip 리스트) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        <Tabs
          value={contentTab}
          onChange={(_, newValue: number) => setContentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'normal',
              px: 2,
              py: 1,
              borderRadius: '8px',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 68, 210, 0.1)',
                color: '#0044D2',
                fontWeight: 'bold',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {CONTENT_TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <PointhomeContentStatusKpiCards data={kpiData} contentName={CONTENT_TABS[contentTab]} />

            <RouletteContentUserStatusChart data={userStatusChartData} />

            <Box
              sx={{
                mt: 3,
                border: '1px solid #e5e7eb',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#ffffff',
              }}
            >
              <RouletteContentDailyCountChart data={dailyChartData} />
              <RouletteContentDailyCountTable
                columnLabels={dailyCountColumnLabels}
                rows={dailyTableRows}
              />
            </Box>

            <Box
              sx={{
                mt: 3,
                border: '1px solid #e5e7eb',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#ffffff',
              }}
            >
              <RouletteContentDailyCountChart
                data={amountChartData}
                title="포인트 지급 분포"
                colorScale="gradient"
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
